import { deleteCharacter as removeCharacter, mergeCharacters, renameCharacter } from "../document-operations";
import { consumeGuidedSample } from "../guided-sample";
import { ACCEPTED_FILES, parserModeForFile, textFromFile } from "../importers";
import { getImportTextSample, renderImportTextSamplePicker } from "../import-text-samples";
import { allEntries, rebuildSections } from "../parser";
import { parseSource } from "../screenplay-format";
import { applyImportDefaults, parseCommaSeparated } from "../script-normalizer";
import { store } from "../store";
import type { EntryKind, ImportMode, ParseResult, ParserMode } from "../types";
import { escapeHtml, LightElement } from "../ui";

export class ScriptImportFlow extends LightElement {
  private step = 1;
  private sourceText = "";
  private sourceName = "";
  private importMode: ImportMode = "text";
  private parserMode: ParserMode = "auto";
  private result?: ParseResult;
  private busy = false;
  private error = "";
  private voices: SpeechSynthesisVoice[] = [];

  connectedCallback(): void {
    try {
      const sample = consumeGuidedSample();
      if (sample) {
        this.sourceText = sample.text;
        this.sourceName = sample.title;
        this.importMode = "text";
      }
    } catch { /* Session storage may be unavailable; normal import remains usable. */ }
    this.voices = "speechSynthesis" in window ? speechSynthesis.getVoices() : [];
    if ("speechSynthesis" in window) speechSynthesis.addEventListener("voiceschanged", () => {
      this.applyReviewFields();
      this.voices = speechSynthesis.getVoices();
      if (this.step === 3) this.render();
    }, { signal: this.abort.signal });
    this.render();
  }

  private render(): void {
    this.innerHTML = `<section class="page import-page">
      <a class="back-link" href="#/">← Library</a>
      <div class="wizard-heading"><p class="eyebrow">Import script</p><h1>${this.heading()}</h1><p>${this.subheading()}</p></div>
      <ol class="steps" aria-label="Import progress">${["Source", "Preview", "Review"].map((label, index) => `<li class="${this.step >= index + 1 ? "is-active" : ""}" ${this.step === index + 1 ? 'aria-current="step"' : ""}><span>${index + 1}</span>${label}</li>`).join("")}</ol>
      ${this.error ? `<div class="notice notice--error" role="alert">${escapeHtml(this.error)}</div>` : ""}
      <div class="wizard-panel">${this.step === 1 ? this.sourceView() : this.step === 2 ? this.previewView() : this.reviewView()}</div>
    </section>`;
    this.bind();
  }

  private heading(): string { return ["Bring in your script", "Check the source", "Review the cast"][this.step - 1] ?? "Import"; }
  private subheading(): string { return ["Choose a file or paste the script text.", "Choose a format and confirm the text before parsing.", "Check the detected roles and parsing results."][this.step - 1] ?? ""; }

  private sourceView(): string {
    return `<div class="source-grid">
      <label class="drop-zone" tabindex="0"><input data-file type="file" accept="${ACCEPTED_FILES}"><span class="drop-zone__icon">↑</span><strong>${this.busy ? "Reading file…" : "Drop a script here"}</strong><span>or tap to choose TXT, Fountain, FDX, DOCX, or PDF</span></label>
      <div class="divider"><span>or</span></div>
      <div class="source-text-column">${renderImportTextSamplePicker()}<label class="field"><span>Paste script text</span><textarea data-source rows="14" placeholder="Alex: Welcome, everyone…">${escapeHtml(this.sourceText)}</textarea></label></div>
    </div>
    <div class="wizard-actions"><a class="button button--ghost" href="#/">Cancel</a><button class="button" data-next ${!this.sourceText.trim() || this.busy ? "disabled" : ""}>Continue</button></div>`;
  }

  private previewView(): string {
    return `<div class="preview-layout"><div>
      <label class="field"><span>Script title</span><input data-title value="${escapeHtml(this.sourceName.replace(/\.(txt|md|fountain|fdx|docx|pdf)$/i, "") || "Untitled Script")}"></label>
      <fieldset class="format-picker"><legend>Script format</legend>
        ${(["auto", "fountain", "fdx", "left", "top", "tag", "none"] as ParserMode[]).map((mode) => `<label><input type="radio" name="parser" value="${mode}" ${this.parserMode === mode ? "checked" : ""}><span><strong>${({ auto: "Auto detect", fountain: "Fountain", fdx: "Final Draft FDX", left: "Speaker on left", top: "Speaker above", tag: "@speaker tags", none: "One role" })[mode]}</strong><small>${({ auto: "Best match", fountain: ".fountain screenplay", fdx: ".fdx screenplay", left: "W.M.: dialogue", top: "NAME then dialogue", tag: "@W.M. dialogue", none: "All text" })[mode]}</small></span></label>`).join("")}
      </fieldset></div>
      <label class="field"><span>Edit original source</span><textarea data-preview rows="22">${escapeHtml(this.sourceText)}</textarea><small>Fix typos or formatting errors here before parsing. This corrected source is preserved.</small></label></div>
      <div class="wizard-actions"><button class="button button--ghost" data-back>Back</button><button class="button" data-parse>Parse script</button></div>`;
  }

  private reviewView(): string {
    if (!this.result) return "";
    const document = this.result.document;
    const entries = allEntries(document);
    return `<div class="review-layout">
      <section><div class="panel-heading"><div><p class="eyebrow">Detected cast</p><h2>${document.characters.length} roles</h2></div></div>
        <div class="character-list">${document.characters.map((character, index) => `<div class="character-row"><input class="colour-input" type="color" data-colour="${character.id}" value="${this.hexColour(character.colour, index)}" aria-label="Colour for ${escapeHtml(character.name)}"><label><span class="sr-only">Character name</span><input data-character="${character.id}" value="${escapeHtml(character.name)}"></label><label><span class="sr-only">Aliases for ${escapeHtml(character.name)}</span><input data-aliases="${character.id}" value="${escapeHtml(character.aliases.join(", "))}" placeholder="Aliases"></label><small>${entries.filter((item) => item.speakerId === character.id).length} lines</small><div class="character-row__options"><label><span class="sr-only">Voice for ${escapeHtml(character.name)}</span><select data-import-voice="${character.id}"><option value="">Default voice</option>${this.voiceOptions(character.voiceId)}</select></label><label><span class="sr-only">Merge ${escapeHtml(character.name)} into</span><select data-merge-target="${character.id}"><option value="">Merge into…</option>${document.characters.filter((target) => target.id !== character.id).map((target) => `<option value="${target.id}">${escapeHtml(target.name)}</option>`).join("")}</select></label><button class="button button--ghost button--tiny" data-merge="${character.id}">Merge</button><button class="icon-button icon-button--danger" data-delete-character="${character.id}" aria-label="Delete ${escapeHtml(character.name)}">×</button></div></div>`).join("") || "<p>No speakers were detected. Go back and try another format.</p>"}</div>
      </section>
      <section><p class="eyebrow">Parse summary</p><h2>${entries.length} entries</h2>
        <dl class="summary-list"><div><dt>Dialogue</dt><dd>${entries.filter((item) => item.kind === "dialogue").length}</dd></div><div><dt>Directions</dt><dd>${entries.filter((item) => item.kind === "stageDirection").length}</dd></div><div><dt>Sections</dt><dd>${document.sections.length}</dd></div><div><dt>Pages</dt><dd>${document.pages.length}</dd></div></dl>
        ${this.result.issues.length ? `<div class="notice"><strong>${this.result.issues.length} items to check</strong><ul>${this.result.issues.slice(0, 5).map((issue) => `<li>Line ${issue.line}: ${escapeHtml(issue.message)}</li>`).join("")}</ul></div>` : '<div class="notice notice--success">No obvious parsing issues found.</div>'}
      </section>
    </div><details class="parse-editor" ${this.result.issues.length ? "open" : ""}><summary>Edit parsed lines and sections <span>${entries.length}</span></summary><div class="parse-editor__list">${entries.map((item, index) => this.editorRow(item, index)).join("")}</div></details>
    <div class="wizard-actions"><button class="button button--ghost" data-back>Back</button><button class="button" data-save>Save and rehearse</button></div>`;
  }

  private bind(): void {
    const fileInput = this.querySelector<HTMLInputElement>("[data-file]");
    if (fileInput) this.listen(fileInput, "change", () => { const file = fileInput.files?.[0]; if (file) void this.readFile(file); });
    const drop = this.querySelector<HTMLElement>(".drop-zone");
    if (drop) {
      this.listen(drop, "dragover", (event) => { event.preventDefault(); drop.classList.add("is-dragging"); });
      this.listen(drop, "dragleave", () => drop.classList.remove("is-dragging"));
      this.listen(drop, "drop", (event) => { event.preventDefault(); const file = event.dataTransfer?.files[0]; if (file) void this.readFile(file); });
    }
    const source = this.querySelector<HTMLTextAreaElement>("[data-source]");
    if (source) this.listen(source, "input", () => { this.sourceText = source.value; const next = this.querySelector<HTMLButtonElement>("[data-next]"); if (next) next.disabled = !source.value.trim(); });
    this.querySelectorAll<HTMLButtonElement>("[data-load-text-sample]").forEach((button) => this.listen(button, "click", () => {
      const sample = getImportTextSample(button.dataset.loadTextSample ?? "");
      if (!sample || !source) return;
      this.sourceText = sample.text;
      this.sourceName = sample.title;
      this.importMode = "text";
      this.parserMode = sample.parserMode;
      source.value = sample.text;
      const next = this.querySelector<HTMLButtonElement>("[data-next]");
      if (next) next.disabled = false;
      const status = this.querySelector<HTMLElement>("[data-import-sample-status]");
      if (status) status.textContent = `${sample.title} loaded. You can edit it before continuing.`;
      source.focus();
    }));
    this.querySelector("[data-next]")?.addEventListener("click", () => { this.step = 2; this.render(); });
    this.querySelector("[data-back]")?.addEventListener("click", () => { this.step -= 1; this.render(); });
    this.querySelectorAll<HTMLInputElement>('input[name="parser"]').forEach((radio) => this.listen(radio, "change", () => { this.parserMode = radio.value as ParserMode; }));
    this.querySelector("[data-parse]")?.addEventListener("click", () => this.parse());
    this.querySelector("[data-save]")?.addEventListener("click", () => void this.save());
    this.querySelectorAll<HTMLButtonElement>("[data-delete-character]").forEach((button) => this.listen(button, "click", () => this.deleteCharacter(button.dataset.deleteCharacter ?? "")));
    this.querySelectorAll<HTMLButtonElement>("[data-merge]").forEach((button) => this.listen(button, "click", () => {
      const sourceId = button.dataset.merge ?? "";
      const targetId = this.querySelector<HTMLSelectElement>(`[data-merge-target="${sourceId}"]`)?.value ?? "";
      if (targetId) this.mergeCharacter(sourceId, targetId);
    }));
  }

  private async readFile(file: File): Promise<void> {
    this.busy = true; this.error = ""; this.render();
    try {
      this.sourceText = await textFromFile(file);
      this.sourceName = file.name;
      this.parserMode = parserModeForFile(file.name) ?? "auto";
      this.importMode = "file";
      this.step = 2;
    }
    catch (error) { this.error = error instanceof Error ? error.message : "Could not read this file."; }
    finally { this.busy = false; this.render(); }
  }

  private parse(): void {
    const source = this.querySelector<HTMLTextAreaElement>("[data-preview]");
    const title = this.querySelector<HTMLInputElement>("[data-title]");
    if (source) this.sourceText = source.value;
    this.sourceName = title?.value.trim() || this.sourceName;
    this.result = parseSource(this.sourceText, this.parserMode, this.importMode, this.sourceName);
    applyImportDefaults(this.result.document, store.state.settings);
    this.step = 3; this.render();
  }

  private voiceOptions(selected?: string): string {
    return this.voices.map((voice) => `<option value="${escapeHtml(voice.voiceURI)}" ${voice.voiceURI === selected ? "selected" : ""}>${escapeHtml(voice.name)} (${escapeHtml(voice.lang)})</option>`).join("");
  }

  private editorRow(item: ReturnType<typeof allEntries>[number], index: number): string {
    if (!this.result) return "";
    const kinds: EntryKind[] = ["dialogue", "stageDirection", "heading", "note", "pageMarker"];
    return `<div class="parse-editor__row">
      <span class="parse-editor__number">${index + 1}</span>
      <label><span class="sr-only">Entry type</span><select data-entry-kind="${item.id}">${kinds.map((kind) => `<option value="${kind}" ${item.kind === kind ? "selected" : ""}>${kind.replace(/([A-Z])/g, " $1")}</option>`).join("")}</select></label>
      <label><span class="sr-only">Speaker</span><select data-entry-speaker="${item.id}"><option value="">No speaker</option>${this.result.document.characters.map((character) => `<option value="${character.id}" ${item.speakerId === character.id ? "selected" : ""}>${escapeHtml(character.name)}</option>`).join("")}</select></label>
      <label><span class="sr-only">Entry text</span><textarea data-entry-text="${item.id}" rows="2">${escapeHtml(item.text)}</textarea></label>
    </div>`;
  }

  private applyReviewFields(): void {
    if (!this.result || this.step !== 3) return;
    const document = this.result.document;
    this.querySelectorAll<HTMLInputElement>("[data-character]").forEach((input) => {
      const character = document.characters.find((item) => item.id === input.dataset.character);
      if (!character || !input.value.trim()) return;
      renameCharacter(document, character.id, input.value);
    });
    this.querySelectorAll<HTMLInputElement>("[data-aliases]").forEach((input) => {
      const character = document.characters.find((item) => item.id === input.dataset.aliases);
      if (character) character.aliases = parseCommaSeparated(input.value);
    });
    this.querySelectorAll<HTMLInputElement>("[data-colour]").forEach((input) => {
      const character = document.characters.find((item) => item.id === input.dataset.colour);
      if (character) character.colour = input.value;
    });
    this.querySelectorAll<HTMLSelectElement>("[data-import-voice]").forEach((select) => {
      const character = document.characters.find((item) => item.id === select.dataset.importVoice);
      if (character) character.voiceId = select.value || undefined;
    });
    const entries = allEntries(document);
    this.querySelectorAll<HTMLTextAreaElement>("[data-entry-text]").forEach((input) => {
      const item = entries.find((entry) => entry.id === input.dataset.entryText);
      if (item) item.text = input.value.trim();
    });
    this.querySelectorAll<HTMLSelectElement>("[data-entry-kind]").forEach((select) => {
      const item = entries.find((entry) => entry.id === select.dataset.entryKind);
      if (item) item.kind = select.value as EntryKind;
    });
    this.querySelectorAll<HTMLSelectElement>("[data-entry-speaker]").forEach((select) => {
      const item = entries.find((entry) => entry.id === select.dataset.entrySpeaker);
      const character = document.characters.find((candidate) => candidate.id === select.value);
      if (!item) return;
      item.speakerId = character?.id;
      item.speakerLabel = character?.name;
    });
    rebuildSections(document);
  }

  private deleteCharacter(characterId: string): void {
    if (!this.result) return;
    this.applyReviewFields();
    const character = this.result.document.characters.find((item) => item.id === characterId);
    if (!character || !confirm(`Delete ${character.name}? Their lines will remain unassigned.`)) return;
    removeCharacter(this.result.document, characterId);
    this.render();
  }

  private mergeCharacter(sourceId: string, targetId: string): void {
    if (!this.result || sourceId === targetId) return;
    this.applyReviewFields();
    const source = this.result.document.characters.find((item) => item.id === sourceId);
    const target = this.result.document.characters.find((item) => item.id === targetId);
    if (!source || !target) return;
    mergeCharacters(this.result.document, sourceId, targetId);
    this.render();
  }

  private hexColour(colour: string | undefined, index: number): string {
    if (colour?.startsWith("#")) return colour;
    const hue = (index * 67 + 250) % 360;
    const canvas = document.createElement("canvas").getContext("2d");
    if (!canvas) return "#8b7cf6";
    canvas.fillStyle = `hsl(${hue} 65% 62%)`;
    return canvas.fillStyle;
  }

  private async save(): Promise<void> {
    if (!this.result) return;
    this.applyReviewFields();
    await store.save(this.result.document);
    location.hash = `#/player/${this.result.document.id}`;
  }
}

customElements.define("script-import-flow", ScriptImportFlow);
